import {
  SnapshotCollection,
  SnapshotCollectionFavoriteResponse,
  SnapshotCollectionPermissionAccess,
  Subcollection,
} from "./snapshotCollection.types";
import { SNAPSHOT_COLLECTION_TAGS } from "@/modules/snapshots/snapshotCollection.constraints";
import { BUSINESS_REPORTS_TAGS } from "@/modules/snapshots/businessReports.constraints";
import { baseApi } from "@/api/baseApi";

export const snapshotCollectionApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    createOrUpdateSnapshotCollection: builder.mutation<
      void,
      { id?: string; business_report_id?: string; name?: string }
    >({
      invalidatesTags: [SNAPSHOT_COLLECTION_TAGS.SNAPSHOT_COLLECTION_TAG],
      query: ({ id, ...payload }) => ({
        url: id
          ? `/pf-mainweb/business-reports/snapshot-collections/${id}`
          : "/pf-mainweb/business-reports/snapshot-collections",
        method: id ? "PUT" : "POST",
        data: payload,
      }),
    }),
    deleteSnapshotCollection: builder.mutation<void, { id: string }>({
      invalidatesTags: [
        SNAPSHOT_COLLECTION_TAGS.SNAPSHOT_COLLECTION_TAG,
        BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG,
      ],
      query: ({ id }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${id}`,
        method: "DELETE",
      }),
    }),
    deleteSnapshotCollectionPermission: builder.mutation<void, { id: string; permissionId: string }>({
      invalidatesTags: [SNAPSHOT_COLLECTION_TAGS.SNAPSHOT_COLLECTION_TAG],
      query: ({ id, permissionId }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${id}/permissions/${permissionId}`,
        method: "DELETE",
      }),
    }),
    createSnapshotCollectionPermission: builder.mutation<void, { id: string; role: string; users: string[] }>({
      invalidatesTags: [SNAPSHOT_COLLECTION_TAGS.SNAPSHOT_COLLECTION_TAG],
      query: ({ id, role, users }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${id}/permissions`,
        method: "POST",
        data: {
          permissions: users.map(user => ({ role, user_id: user, business_report_outcome_id: id })),
        },
      }),
    }),
    updateSnapshotCollectionAccess: builder.mutation<void, { id: string; access: SnapshotCollectionPermissionAccess }>({
      invalidatesTags: [SNAPSHOT_COLLECTION_TAGS.SNAPSHOT_COLLECTION_TAG],
      query: ({ id, access }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${id}/permissions/access`,
        method: "PATCH",
        data: { access },
      }),
    }),
    getSnapshotCollections: builder.query<
      SnapshotCollection[],
      { business_report_id?: string; exclude_pipeline_managed?: boolean } | void
    >({
      providesTags: [SNAPSHOT_COLLECTION_TAGS.SNAPSHOT_COLLECTION_TAG],
      query: params => ({
        url: "/pf-mainweb/business-reports/snapshot-collections",
        params,
      }),
    }),
    getSnapshotCollectionFavorites: builder.query<SnapshotCollectionFavoriteResponse[], void>({
      providesTags: [SNAPSHOT_COLLECTION_TAGS.SNAPSHOT_COLLECTION_TAG],
      query: () => ({
        url: "/pf-mainweb/business-reports/snapshot-collections/favorites",
      }),
    }),
    addSnapshotCollectionFavorite: builder.mutation<void, { snapshot_collection_id: string }>({
      invalidatesTags: [SNAPSHOT_COLLECTION_TAGS.SNAPSHOT_COLLECTION_TAG],
      query: ({ snapshot_collection_id }) => ({
        url: "/pf-mainweb/business-reports/snapshot-collections/favorites",
        method: "POST",
        data: { snapshot_collection_id },
      }),
    }),
    deleteSnapshotCollectionFavorite: builder.mutation<void, { snapshot_collection_id: string }>({
      invalidatesTags: [SNAPSHOT_COLLECTION_TAGS.SNAPSHOT_COLLECTION_TAG],
      query: ({ snapshot_collection_id }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshot_collection_id}/favorites`,
        method: "DELETE",
      }),
    }),
    getSubcollections: builder.query<
      Subcollection[],
      { snapshot_collection_id: string; contract_family_projection?: boolean }
    >({
      providesTags: [SNAPSHOT_COLLECTION_TAGS.SUBCOLLECTION_TAG],
      query: ({ snapshot_collection_id, contract_family_projection }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshot_collection_id}/subcollections`,
        params: { contract_family_projection },
      }),
    }),
    createSubcollection: builder.mutation<
      Subcollection,
      {
        snapshot_collection_id: string;
        name: string;
        business_report_outcome_ids: string[];
        contract_family_projection?: boolean;
      }
    >({
      invalidatesTags: [SNAPSHOT_COLLECTION_TAGS.SUBCOLLECTION_TAG, BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ snapshot_collection_id, ...data }) => ({
        url: `/pf-mainweb/business-reports/snapshot-collections/${snapshot_collection_id}/subcollections`,
        method: "POST",
        data,
      }),
    }),
    renameSubcollection: builder.mutation<void, { id: string; name: string }>({
      invalidatesTags: [SNAPSHOT_COLLECTION_TAGS.SUBCOLLECTION_TAG],
      query: ({ id, name }) => ({
        url: `/pf-mainweb/business-reports/subcollections/${id}`,
        method: "PATCH",
        data: { name },
      }),
    }),
    deleteSubcollection: builder.mutation<void, { id: string }>({
      invalidatesTags: [SNAPSHOT_COLLECTION_TAGS.SUBCOLLECTION_TAG, BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ id }) => ({
        url: `/pf-mainweb/business-reports/subcollections/${id}`,
        method: "DELETE",
      }),
    }),
    addSubcollectionMembers: builder.mutation<
      void,
      { id: string; business_report_outcome_ids: string[]; contract_family_projection?: boolean }
    >({
      invalidatesTags: [SNAPSHOT_COLLECTION_TAGS.SUBCOLLECTION_TAG, BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ id, ...data }) => ({
        url: `/pf-mainweb/business-reports/subcollections/${id}/members`,
        method: "POST",
        data,
      }),
    }),
    deleteSubcollectionMembers: builder.mutation<
      void,
      { id: string; business_report_outcome_ids: string[]; contract_family_projection?: boolean }
    >({
      invalidatesTags: [SNAPSHOT_COLLECTION_TAGS.SUBCOLLECTION_TAG, BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_OUTCOME_TAG],
      query: ({ id, ...data }) => ({
        url: `/pf-mainweb/business-reports/subcollections/${id}/members`,
        method: "DELETE",
        data,
      }),
    }),
  }),
});

export const {
  useGetSnapshotCollectionsQuery,
  useUpdateSnapshotCollectionAccessMutation,
  useCreateOrUpdateSnapshotCollectionMutation,
  useDeleteSnapshotCollectionPermissionMutation,
  useCreateSnapshotCollectionPermissionMutation,
  useDeleteSnapshotCollectionMutation,
  useGetSnapshotCollectionFavoritesQuery,
  useAddSnapshotCollectionFavoriteMutation,
  useDeleteSnapshotCollectionFavoriteMutation,
  useGetSubcollectionsQuery,
  useCreateSubcollectionMutation,
  useRenameSubcollectionMutation,
  useDeleteSubcollectionMutation,
  useAddSubcollectionMembersMutation,
  useDeleteSubcollectionMembersMutation,
} = snapshotCollectionApi;
